import SimpleLinearRegression from 'ml-regression-simple-linear';
import { stateNames, attributeOptions } from './Helpers';

export const getClimateData = (climAttr, area_reduce) => {
  // Fetch climate data for an attribute
  // if area_reduce is state_mean, fetch data for states
  // if area_reduce is county_mean, fetch data for counties

  let params = attributeOptions[climAttr]['params'];
  params['elems'][0]['area_reduce'] = area_reduce;

  return fetch(`${window.location.protocol}//grid2.rcc-acis.org/GridData`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/JSON' },
    body: JSON.stringify(params),
  })
    .then((res) => res.json())
    .then(({ data }) => {
      let result = { years: [], [climAttr]: {} };
      data.forEach(([year, dataObj]) => {
        result.years.push(parseInt(year));
        Object.entries(dataObj).forEach(([location, num]) => {
          if (area_reduce === 'county_mean' || stateNames.includes(location)) {
            if (!result[climAttr][location]) result[climAttr][location] = [];
            result[climAttr][location].push(num);
          }
        });
      });
      return result;
    });
};

export const getClimateTrends = (data, start, end, minYear) => {
  //get the trend/decade for each state/county of an attr given the start and the end year
  let startIndex = start - minYear;
  let endIndex = end - minYear;
  let x = [];

  for (let i = start; i <= end; i++) {
    x.push(i);
  }

  let result = {};
  Object.entries(data).forEach(([attr, attrData]) => {
    if (attr !== 'years') {
      Object.entries(attrData).forEach(([location, locationData]) => {
        let y = locationData.slice(startIndex, endIndex + 1);

        let trend = new SimpleLinearRegression(x, y).slope * 10;
        if (!result[attr]) result[attr] = {};
        result[attr][location] = trend;
      });
    }
  });

  return result;
};

export const getClimateProjection = (location, attr, grid, emission) => {
  let thisAttr = attributeOptions[attr];
  let month = thisAttr['month'];
  let thisAttrElems = thisAttr['params']['elems'][0];
  let a_r = location.length > 2 ? 'county_mean' : 'state_mean';

  let data = {
    state: location.length <= 2 ? location : undefined,
    county: location.length > 2 ? location : undefined,
    grid: `loca:${grid}:${emission}`,
    sdate: 1951 + month,
    edate: 2099 + month,
    elems: [
      {
        name: thisAttrElems['name'],
        interval: thisAttrElems['interval'],
        duration: thisAttrElems['duration'],
        reduce: thisAttrElems['reduce'],
        area_reduce: a_r,
      },
    ],
  };

  if (attr === 'runmaxpcpnlt01') {
    data['elems'][0]['interval'] = [1];
    data['elems'][0]['duration'] = 1;
  } else if (month !== '') {
    data['elems'] = [
      {
        elem: {
          name: thisAttrElems['name'],
          interval: [0, 1],
          duration: 1,
          reduce: thisAttrElems['reduce'],
          area_reduce: a_r,
        },
        interval: thisAttrElems['interval'],
        duration: thisAttrElems['duration'],
        reduce: attr.includes('temp') ? 'mean' : 'sum',
        area_reduce: a_r,
      },
    ];
  }

  return fetch(`${window.location.protocol}//grid2.rcc-acis.org/GridData`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/JSON' },
    body: JSON.stringify(data),
  })
    .then((res) => {
      return res.json();
    })
    .then(({ data }) => {
      let result = { x: [], y: [] };
      data.forEach((year) => {
        result.x.push(year[0].slice(0, 4));
        result.y.push(year[1][location]);
      });

      return result;
    });
};

export const getHazardData = (level, hazard) => {
  let queries = [];
  let year = 1996;
  let currentYear = new Date().getFullYear();

  if (hazard === 'Hurricane') hazard = 'Hurricane (Typhoon)';

  while (year < currentYear) {
    let query = `
      query{
        get${level}Type (
          state: "${stateNames}"
          type: "${hazard}"
          beginTime: "${year}-01-01"
          endTime: "${year + 1}-01-01"
        ) {
          ${level.toLowerCase()}
          count
          damageCrops
          damageProperty
          deaths
          injuries
          episodes
        }
      }
    `;

    queries.push(
      fetch('https://storm.nrcc-acis.org/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
      }).then((res) => res.json())
    );
    year++;
  }

  return Promise.all(queries).then((res) => {
    let result = {};
    res.forEach((year, index) => {
      if (!year.errors) {
        let data = year['data'][`get${level}Type`];
        data.forEach(({ state, county, ...rest }) => {
          Object.entries(rest).forEach(([attr, value]) => {
            if (!result[attr]) {
              result[attr] = {};
            }
            let location = state || county;
            if (!result[attr][location]) result[attr][location] = [];
            result[attr][location].push([1996 + index, value]);
          });
        });
      }
    });
    return result;
  });
};

export const getHazardMapData = (data, start, end) => {
  let result = {};
  Object.entries(data).forEach(([attr, attrData]) => {
    result[attr] = {};
    Object.entries(attrData).forEach(([location, locationData]) => {
      result[attr][location] = 0;

      locationData.forEach(([year, num]) => {
        if (year >= start && year <= end) {
          result[attr][location] += num;
        }
      });
    });
  });
  return result;
};

export const getHazardNarrative = (episodes, damageType) => {
  let queries = [];
  let hazard1 = '';
  let hazard2 = '';
  let filter = '';

  if (damageType[1] === 'deaths') {
    hazard1 = 'deathsDirect';
    hazard2 = 'deathsIndirect';
  } else if (damageType[1] === 'injuries') {
    hazard1 = 'injuryDirect';
    hazard2 = 'injuryIndirect';
  } else if (damageType[1] === 'count') {
    hazard1 = `
      deathsDirect
      deathsIndirect
      injuryDirect
      injuryIndirect
      damageCrops
      damageProperty
    `;
  } else {
    hazard1 = damageType[1];
  }

  if (hazard2 !== '') {
    filter = `filter: { 
      type: { eq: "${damageType[0]}" }
      and: { ${hazard1}: {gt: 0}
      or: { ${hazard2}: {gt: 0}}} }`;
  } else if (hazard1 !== '') {
    filter = `filter: {
      type: { eq: "${damageType[0]}" }
      and: { ${hazard1}: {gt: 0}} }`;
  } else {
    filter = `filter: {
      type: { eq: "${damageType[0]}" }}`;
  }

  episodes.forEach((episode) => {
    let query =
      damageType[1] === 'count'
        ? `
        query{
          getEpisode(episode: "${String(episode)}") {
            episode
            beginTime
            endTime
            events {
              event
              episode
              state
              counties
              type
              beginTime
              endTime
              ${hazard1}
              narrative
            }
            narrative
          }
        }
    `
        : `
        query{
          getEpisode(episode: "${String(episode)}") {
            episode
            beginTime
            endTime
            events(${filter}) {
              event
              episode
              state
              counties
              type
              beginTime
              endTime
              ${hazard1}
              ${hazard2}
              narrative
            }
            narrative
          }
        }
        `;

    queries.push(
      fetch('https://storm.nrcc-acis.org/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
      }).then((res) => res.json())
    );
  });

  return Promise.all(queries).then((res) => {
    let result = {};

    res.forEach((epi) => {
      if (!epi.errors && epi.data?.getEpisode && epi.data.getEpisode?.episode) {
        result[epi.data.getEpisode.episode] = {
          beginTime: epi.data.getEpisode.beginTime,
          endTime: epi.data.getEpisode.endTime,
          narrative: epi.data.getEpisode.narrative,
          events: epi.data.getEpisode.events,
        };
      }
    });

    return result;
  });
};

export async function getCountyName(fips) {
  let query = `
    query{
      getCounty(fips: "${fips}") {
        name
      }
    }
    `;
  let name = await fetch('https://storm.nrcc-acis.org/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query }),
  })
    .then((res) => res.json())
    .then((result) =>
      result.errors
        ? 'Error'
        : result.data.getCounty
        ? result.data.getCounty.name
        : 'No county reported'
    );
  return name;
}

const bbox = [-98.85, 31.65, -71.13, 49.82];
export const search = async (terms) => {
  let results = await fetch(
    `https://api.mapbox.com/search/geocode/v6/forward?access_token=pk.eyJ1IjoiYmVuZWNrIiwiYSI6ImNrczd4ZGhjOTB0N3MzMGxobHQ4aXp0bm0ifQ.yRPTkmXuTvIvOmY-IrsYQg&country=US&bbox=${bbox.join(',')}&q=${terms.trim()}`
  );

  if (!results.ok) {
    throw new Error('Error');
  }

  results = await results.json();
  let location = results.features[0];

  let name, lat, lng;
  if (location) {
    name = location.properties.full_address.replace(', United States','');
    lat = location.properties.coordinates.latitude;
    lng = location.properties.coordinates.longitude;
  }

  return {
    lat,
    lng,
    zoom: 7,
    name: name || `No results for: ${terms.trim()}`,
  };
};